import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, BalanceCheckBanner, useFinancialYears, downloadReportCsv, formatCurrency, formatBalance, formatDateDMY } from "./reportShared";

/**
 * Phase 8 - Member Receivables Report (items 26-28). Per-member Opening /
 * Bills / Receipts / Adjustments / Advances / Closing Outstanding / Aging,
 * built from the same Member Receivables / Member Advances control ledgers
 * Billing integration (Phase 6) and Opening Balances (Phase 7) already post
 * to. The Member Detail Total vs. control ledger check at the bottom (item
 * 27) is never hidden. "View Statement" (item 28) opens one member's full
 * running statement in a dialog.
 */
export default function MemberReceivablesReportPage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statementFor, setStatementFor] = useState(null);

  const params = { financialYearId, fromDate, toDate };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/receivables", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load member receivables report"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, fromDate, toDate]);

  const rows = (data?.rows || []).filter((r) => !search || `${r.ownerName} ${r.wing} ${r.flatNumber}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Member Receivables Report</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Per-member outstanding, built from posted Billing accounting activity.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          search={search} onSearchChange={setSearch} searchPlaceholder="Member or flat"
          onExport={() => downloadReportCsv("/society/accounting/reports/receivables", params, `member-receivables-${data?.toDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !data?.configured ? (
          <EmptyState>{data?.message || "Billing Ledger Mapping has no Member Receivables ledger configured yet."}</EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState>No member accounting history yet.</EmptyState>
        ) : (
          <>
            <SectionCard subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead><TableHead>Flat</TableHead>
                      <TableHead className="text-right">Opening</TableHead><TableHead className="text-right">Bills</TableHead>
                      <TableHead className="text-right">Receipts</TableHead><TableHead className="text-right">Adjustments</TableHead>
                      <TableHead className="text-right">Advance</TableHead><TableHead className="text-right">Closing Outstanding</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.partyLabel}>
                        <TableCell className="font-semibold">{r.ownerName}</TableCell>
                        <TableCell>{r.wing}-{r.flatNumber}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.openingBalance)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.bills)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.receipts)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.adjustments)}</TableCell>
                        <TableCell className="text-right">{r.advance ? formatCurrency(r.advance) : "—"}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(r.closingOutstanding)}</TableCell>
                        <TableCell><button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={() => setStatementFor(r)}>View Statement</button></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-[#111827] font-bold">
                      <TableCell colSpan={7}>Member Detail Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.detailTotal)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
            <BalanceCheckBanner label="Member Receivables: Detail Total vs Control Ledger" difference={data.controlBalance - data.detailTotal} okLabel="Reconciled" />
          </>
        )}
      </div>

      {data?.configured && rows.length > 0 && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Member Receivables Report" rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead><tr className="border-b border-black"><th className="py-1 text-left">Member</th><th className="py-1 text-left">Flat</th><th className="py-1 text-right">Opening</th><th className="py-1 text-right">Bills</th><th className="py-1 text-right">Receipts</th><th className="py-1 text-right">Closing</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.partyLabel} className="border-b border-gray-300">
                    <td className="py-0.5">{r.ownerName}</td><td className="py-0.5">{r.wing}-{r.flatNumber}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.openingBalance)}</td><td className="py-0.5 text-right">{formatCurrency(r.bills)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.receipts)}</td><td className="py-0.5 text-right">{formatCurrency(r.closingOutstanding)}</td>
                  </tr>
                ))}
                <tr className="border-t border-black font-bold"><td colSpan={5} className="py-1">Detail Total</td><td className="py-1 text-right">{formatCurrency(data.detailTotal)}</td></tr>
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}

      {statementFor && (
        <MemberStatementDialog
          member={statementFor} financialYearId={financialYearId} fromDate={fromDate} toDate={toDate}
          onClose={() => setStatementFor(null)}
          onOpenLedger={(ledgerId) => navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}`)}
        />
      )}
    </div>
  );
}

function MemberStatementDialog({ member, financialYearId, fromDate, toDate, onClose, onOpenLedger }) {
  const navigate = useNavigate();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get("/society/accounting/reports/receivables/statement", {
          params: { ownerId: member.ownerId, wing: member.wing, flatNumber: member.flatNumber, ownerName: member.ownerName, financialYearId, fromDate, toDate },
        });
        setStatement(res.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load member statement"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Statement — {member.ownerName} (Flat {member.wing}-{member.flatNumber})</DialogTitle></DialogHeader>
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !statement ? (
          <div className="text-sm text-[#6B7280]">No data.</div>
        ) : (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#6B7280]">Opening Receivable:</span> <span className="font-semibold">{formatBalance(statement.openingBalance)}</span></div>
              <div><span className="text-[#6B7280]">Closing Receivable:</span> <span className="font-semibold">{formatBalance(statement.closingBalance)}</span></div>
              {!!statement.advanceLedgerId && (
                <>
                  <div><span className="text-[#6B7280]">Opening Advance:</span> <span className="font-semibold">{formatCurrency(statement.openingAdvance)}</span></div>
                  <div><span className="text-[#6B7280]">Closing Advance:</span> <span className="font-semibold">{formatCurrency(statement.closingAdvance)}</span></div>
                </>
              )}
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Receivable — Bills &amp; Receipts</div>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Particulars</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {statement.rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                        <TableCell>{row.voucherId ? <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber}</button> : "-"}</TableCell>
                        <TableCell>{row.narration}</TableCell>
                        <TableCell className="text-right">{row.debit ? formatCurrency(row.debit) : "—"}</TableCell>
                        <TableCell className="text-right">{row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.runningBalance)}</TableCell>
                      </TableRow>
                    ))}
                    {statement.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#6B7280]">No transactions in this range.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              {statement.ledgerId && <button type="button" className="mt-1 text-xs font-semibold text-[#111827] hover:underline" onClick={() => onOpenLedger(statement.ledgerId)}>Open full Member Receivables ledger &rarr;</button>}
            </div>
            {!!statement.advanceLedgerId && statement.advanceRows.length > 0 && (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Advance</div>
                <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Particulars</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Advance Balance</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {statement.advanceRows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                          <TableCell>{row.voucherId ? <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber}</button> : "-"}</TableCell>
                          <TableCell>{row.narration}</TableCell>
                          <TableCell className="text-right">{row.debit ? formatCurrency(row.debit) : "—"}</TableCell>
                          <TableCell className="text-right">{row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.runningBalance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <button type="button" className="mt-1 text-xs font-semibold text-[#111827] hover:underline" onClick={() => onOpenLedger(statement.advanceLedgerId)}>Open full Member Advances ledger &rarr;</button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
